/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { ReactNode } from 'react';
import cx from 'classnames';
import { Pagination } from 'sarsaparilla';
import SearchPaginationText from './SearchPaginationText';

interface SearchPaginationProps {
    /** This is the index of the first item on the current page. For example, if the page size is 5, the start value for the third page would be 15 */
    start: number | null;
    /** The total number of items being paginated (not the total number of pages) */
    total: number | null;
    /** The number of items per page */
    pageSize: number | null;
    /** change handler called with the index of the next active page index */
    onPage: (page: number) => void;
    /** Set max total page size */
    maxResults?: number;
    /** Optional content rendered alongside the results-count text */
    resultsTextSlot?: ReactNode;
}

export default function SearchPagination({
    start: startProp,
    total: totalProp,
    pageSize: pageSizeProp,
    onPage,
    maxResults = 300, // this value was once hard-coded and 300 was the previous value used in non-search services
    resultsTextSlot,
}: SearchPaginationProps) {
    // Because some silly people are sending null values ...
    const start = startProp ?? 0;
    const total = totalProp ?? 0;
    const initialPageSize = pageSizeProp ?? 20;

    // Calculate pagination values from the raw prop data
    const pageSize = total < initialPageSize ? total : initialPageSize;
    const pageCount = maxResults
        ? Math.ceil(Math.min(total, maxResults) / pageSize)
        : Math.ceil(total / pageSize);
    const activePage = Math.floor(start / pageSize);
    const hasPagination = pageCount > 1;

    return (
        <div
            className="search-pagination-wrap"
            aria-describedby="search-pagination-results"
        >
            <div
                className={cx('search-pagination', 'search-pagination--inline', {
                    'search-pagination--has-map-reset': resultsTextSlot,
                })}
                id="search-pagination-results"
            >
                <SearchPaginationText
                    start={start}
                    pageSize={pageSize}
                    total={total}
                    maxResults={maxResults}
                >
                    {resultsTextSlot}
                </SearchPaginationText>

                {hasPagination ? (
                    <div>
                        <Pagination
                            aria-label="Search Results Pagination"
                            pages={pageCount}
                            activePage={activePage}
                            onChange={(newPage) => {
                                if (onPage) {
                                    onPage(newPage + 1);
                                }
                            }}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
